import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Clock, MapPin, Save, Link2, Eye, FileText,
  ChevronDown, ChevronRight, Check, Bell, ListOrdered, Plus, Trash2,
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import VideoGuidaVerbali from '../../components/verbali/VideoGuidaVerbali';
import {
  getRepEventTypes,
  getRepEvent,
  createRepEvent,
  updateRepEvent,
  getMyProfile,
  getRepEventCaricheOptions,
  getRepEventAudiencePreview,
} from '../../services/representativeService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EventType {
  _id: string;
  nome: string;
  colore: string;
  icona?: string;
  ambito?: 'esterno' | 'provinciale' | 'regionale' | 'nazionale';
}

// Ambito di creazione (opzione B): un autore crea solo tipi con ambito ≤ al livello
// della sua carica. esterno(0) < provinciale(1) < regionale(2) < nazionale(3).
const AMBITO_RANK: Record<string, number> = { esterno: 0, provinciale: 1, regionale: 2, nazionale: 3 };
const caricaCreateRank = (categoria?: string): number => {
  if (categoria && ['vertice', 'segreteria_nazionale', 'organo_controllo'].includes(categoria)) return 3;
  if (categoria === 'regionale') return 2;
  if (categoria === 'provinciale') return 1;
  return 0; // esterno / sconosciuta
};

interface CaricaEntry {
  caricaId: { _id: string; nome: string; categoria: string; settore: string; livello: number } | null;
  dataInizio: string;
  dataFine: string | null;
}

interface RepProfile {
  _id: string;
  nome: string;
  cognome: string;
  tipo: 'interno' | 'esterno';
  foto?: string;
  associatesAccess?: boolean;
  cariche: CaricaEntry[];
  permissions?: { name: string; description: string }[];
}

interface EventForm {
  nome: string;
  tipologiaId: string;
  authorCaricaId: string;
  dataEvento: string;
  oraInizio: string;
  oraFine: string;
  luogo: string;
  indirizzo: string;
  linkMappa: string;
  descrizione: string;
  noteInterne: string;
  settori: string[];
  reminder24h: boolean;
  targetAudience: {
    scope: string;
    livelli: string[];
    cariche: string[];
    regioni: string[];
    province: string[];
  };
}

interface CaricaOption {
  _id: string;
  nome: string;
  categoria: string;
  settore: string | null;
  livello: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIME_OPTIONS: string[] = (() => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return opts;
})();

const LIVELLO_OPTIONS = [
  { value: 'provinciale', label: 'Provinciale' },
  { value: 'regionale', label: 'Regionale' },
  { value: 'segreteria_nazionale', label: 'Segreteria Nazionale' },
  { value: 'vertice', label: 'Vertice' },
];

const SETTORE_EVENT_LABELS: Record<string, string> = {
  studi: 'Studi e consulenza',
  autoscuole: 'Autoscuole',
  nautica: 'Nautica',
};

const SETTORE_PERM_TO_VALUE: Record<string, string> = {
  'associativa.settore.studi': 'studi',
  'associativa.settore.autoscuole': 'autoscuole',
  'associativa.settore.nautica': 'nautica',
};

const ALL_EVENT_SETTORI = ['studi', 'autoscuole', 'nautica'];

const INITIAL_FORM: EventForm = {
  nome: '',
  tipologiaId: '',
  authorCaricaId: '',
  dataEvento: '',
  oraInizio: '',
  oraFine: '',
  luogo: '',
  indirizzo: '',
  linkMappa: '',
  descrizione: '',
  noteInterne: '',
  settori: [],
  reminder24h: false,
  targetAudience: { scope: 'tutti', livelli: [], cariche: [], regioni: [], province: [] },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AssociativeEventEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const isEdit = Boolean(id);

  // Form state
  const [form, setForm] = useState<EventForm>({ ...INITIAL_FORM });
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [profile, setProfile] = useState<RepProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Ordine del giorno: '' = non ancora scelto (evento nuovo, o evento di prima del campo:
  // il suo ordine del giorno, se c'è, è nella descrizione e resta com'è), 'si' con i punti, 'no'.
  const [haOdg, setHaOdg] = useState<'' | 'si' | 'no'>('');
  const [puntiOdg, setPuntiOdg] = useState<string[]>(['']);

  // Dettagli facoltativi a scomparsa (aperti in modifica per mostrare i dati esistenti)
  const [showOptional, setShowOptional] = useState(isEdit);

  // Cariche selezionabili come destinatari (sezione Visibilita)
  const [caricheOptions, setCaricheOptions] = useState<CaricaOption[]>([]);

  // Anteprima destinatari (chi vedra l'evento)
  const [audiencePreview, setAudiencePreview] = useState<{ total: number; attivi: number; multiCariche: number; perCarica: { nome: string; count: number }[] } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Settori assegnabili all'evento: derivano dai permessi settore.* dell'autore
  const authorSettori = useMemo(() => {
    const values = new Set(
      (profile?.permissions || []).map(p => SETTORE_PERM_TO_VALUE[p.name]).filter(Boolean)
    );
    return ALL_EVENT_SETTORI.filter(s => values.has(s));
  }, [profile]);

  // La visibilita (targetAudience) e modificabile solo con il permesso dedicato
  const canSetTarget = useMemo(
    () => (profile?.permissions || []).some(p => p.name === 'associativa.eventi.visibilita'),
    [profile]
  );

  // Permesso pubblicazione diretta live (per la voce di menu della shell)
  const canLive = useMemo(
    () => (profile?.permissions || []).some(p => p.name === 'associativa.live.publish'),
    [profile]
  );
  const canCircolari = useMemo(
    () => (profile?.permissions || []).some(p => p.name === 'associativa.circolari.read'),
    [profile]
  );
  const canSondaggi = useMemo(
    () => (profile?.permissions || []).some(p => p.name === 'associativa.sondaggi.read'),
    [profile]
  );
  const canCalendario = useMemo(
    () => (profile?.permissions || []).some(p => p.name === 'associativa.eventi.read'),
    [profile]
  );

  const highestCarica = useMemo(() => {
    if (!profile) return undefined;
    const active = profile.cariche.filter(c => !c.dataFine);
    const sorted = active.sort((a, b) => (b.caricaId?.livello || 0) - (a.caricaId?.livello || 0));
    return sorted[0]?.caricaId?.nome;
  }, [profile]);

  // Cariche attive con cui l'autore può pubblicare l'evento (snapshot, ADR 0025).
  const activeCariche = useMemo(() => {
    if (!profile) return [] as { id: string; nome: string; categoria: string }[];
    return profile.cariche
      .filter(c => !c.dataFine && c.caricaId)
      .map(c => ({ id: c.caricaId!._id, nome: c.caricaId!.nome, categoria: c.caricaId!.categoria }));
  }, [profile]);

  // Tipologie creabili dalla carica autrice selezionata (ambito ≤ livello carica).
  // In modifica include comunque la tipologia corrente (per non nasconderla).
  const creatableTypes = useMemo(() => {
    const cat = activeCariche.find(c => c.id === form.authorCaricaId)?.categoria;
    const rank = caricaCreateRank(cat);
    const filtered = eventTypes.filter(t => rank >= (AMBITO_RANK[t.ambito || 'nazionale'] ?? 3));
    if (isEdit && form.tipologiaId && !filtered.some(t => t._id === form.tipologiaId)) {
      const cur = eventTypes.find(t => t._id === form.tipologiaId);
      if (cur) return [cur, ...filtered];
    }
    return filtered;
  }, [eventTypes, activeCariche, form.authorCaricaId, form.tipologiaId, isEdit]);

  const selectedType = useMemo(
    () => eventTypes.find(t => t._id === form.tipologiaId),
    [eventTypes, form.tipologiaId]
  );

  // Opzioni cariche per la sezione Visibilita (nazionali)
  useEffect(() => {
    if (!profile?._id) return;
    getRepEventCaricheOptions()
      .then(res => { if (res.success) setCaricheOptions(res.data || []); })
      .catch(() => { /* la sezione resta usabile senza l'elenco completo */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?._id]);

  // -----------------------------------------------------------------------
  // Load initial data
  // -----------------------------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }

    const load = async () => {
      try {
        const [typesRes, profileRes] = await Promise.all([
          getRepEventTypes(),
          getMyProfile(),
        ]);

        if (typesRes.success) setEventTypes(typesRes.data || []);
        if (profileRes.success) setProfile(profileRes.data);

        // Default settori = tutti quelli dei permessi dell'autore (deselezionabili)
        const permSettori = ALL_EVENT_SETTORI.filter(s =>
          (profileRes.data?.permissions || []).some((p: { name: string }) => SETTORE_PERM_TO_VALUE[p.name] === s)
        );
        // Cariche attive dell'autore: se è una sola, è la carica autrice implicita.
        const activeCar = (profileRes.data?.cariche || [])
          .filter((c: CaricaEntry) => !c.dataFine && c.caricaId)
          .map((c: CaricaEntry) => c.caricaId!._id);
        if (!isEdit) {
          setForm(prev => ({
            ...prev,
            settori: permSettori,
            authorCaricaId: activeCar.length === 1 ? activeCar[0] : '',
          }));
        }

        if (isEdit && id) {
          const eventRes = await getRepEvent(id);
          if (eventRes.success && eventRes.data) {
            const ev = eventRes.data;
            if (Array.isArray(ev.ordineDelGiorno)) {
              const punti = ev.ordineDelGiorno.filter((x: unknown) => typeof x === 'string' && x.trim());
              setHaOdg(punti.length ? 'si' : 'no');
              setPuntiOdg(punti.length ? punti : ['']);
            }
            setForm({
              nome: ev.nome || '',
              tipologiaId: ev.tipologiaId?._id || ev.tipologiaId || '',
              // se l'evento non ha la carica autrice (legacy) e l'autore ne ha una sola, deducila
              authorCaricaId: ev.authorCaricaId?._id || ev.authorCaricaId || (activeCar.length === 1 ? activeCar[0] : ''),
              dataEvento: ev.dataEvento ? ev.dataEvento.substring(0, 10) : '',
              oraInizio: ev.oraInizio || '',
              oraFine: ev.oraFine || '',
              luogo: ev.luogo || '',
              indirizzo: ev.indirizzo || '',
              linkMappa: ev.linkMappa || '',
              descrizione: ev.descrizione || '',
              noteInterne: ev.noteInterne || '',
              // evento legacy senza array settori → propone i settori dei permessi
              settori: Array.isArray(ev.settori) ? ev.settori : permSettori,
              // I rappresentanti hanno solo il promemoria 24h: il toggle è acceso
              // se i reminder automatici sono abilitati per questo evento.
              reminder24h: !!(ev.reminderAutoEnabled && ev.reminderAuto24h),
              targetAudience: {
                scope: ev.targetAudience?.scope || 'tutti',
                livelli: ev.targetAudience?.livelli || [],
                cariche: (ev.targetAudience?.cariche || []).map((c: any) => (typeof c === 'string' ? c : c?._id)).filter(Boolean),
                regioni: ev.targetAudience?.regioni || [],
                province: ev.targetAudience?.province || [],
              },
            });
          }
        }
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem('representative_token');
          navigate('/accesso-associati');
          return;
        }
        toast.error('Errore nel caricamento dei dati');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, navigate]);

  // -----------------------------------------------------------------------
  // Handlers — form
  // -----------------------------------------------------------------------

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleTargetScopeChange = (scope: string) => {
    setForm(prev => ({
      ...prev,
      targetAudience: {
        ...prev.targetAudience,
        scope,
        ...(scope === 'tutti' ? { livelli: [], cariche: [], regioni: [], province: [] } : {}),
      },
    }));
    setAudiencePreview(null);
  };

  const handleLivelloToggle = (livello: string) => {
    setForm(prev => {
      const current = prev.targetAudience.livelli;
      const enabling = !current.includes(livello);
      const next = enabling ? [...current, livello] : current.filter(l => l !== livello);
      // Selezionare un livello (de)seleziona automaticamente le cariche di quella categoria
      const idsOfLivello = caricheOptions.filter(c => c.categoria === livello).map(c => c._id);
      const cariche = enabling
        ? Array.from(new Set([...prev.targetAudience.cariche, ...idsOfLivello]))
        : prev.targetAudience.cariche.filter(id => !idsOfLivello.includes(id));
      return { ...prev, targetAudience: { ...prev.targetAudience, livelli: next, cariche } };
    });
    setAudiencePreview(null);
  };

  const handleCaricaToggle = (caricaId: string) => {
    setForm(prev => {
      const current = prev.targetAudience.cariche;
      const next = current.includes(caricaId)
        ? current.filter(c => c !== caricaId)
        : [...current, caricaId];
      return { ...prev, targetAudience: { ...prev.targetAudience, cariche: next } };
    });
    setAudiencePreview(null);
  };

  const handleTargetTextChange = (field: 'regioni' | 'province', value: string) => {
    const arr = value.split(',').map(s => s.trim()).filter(Boolean);
    setForm(prev => ({
      ...prev,
      targetAudience: { ...prev.targetAudience, [field]: arr },
    }));
    setAudiencePreview(null);
  };

  const handleSettoreEventoToggle = (settore: string) => {
    setForm(prev => ({
      ...prev,
      settori: prev.settori.includes(settore)
        ? prev.settori.filter(s => s !== settore)
        : [...prev.settori, settore],
    }));
    setAudiencePreview(null);
  };

  const loadAudiencePreview = async () => {
    if (!form.tipologiaId) { toast.error('Seleziona prima la tipologia'); return; }
    // authorCaricaId: se vuoto e l'autore ha una sola carica, lo deduce il backend;
    // se ne ha piu di una senza scelta, il backend risponde con un messaggio chiaro.
    setLoadingPreview(true);
    try {
      const res = await getRepEventAudiencePreview(form.targetAudience, form.settori, form.authorCaricaId, form.tipologiaId);
      if (res.success) setAudiencePreview(res.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Errore nel calcolo dei destinatari");
    } finally {
      setLoadingPreview(false);
    }
  };

  // -----------------------------------------------------------------------
  // Validation + payload + submit
  // -----------------------------------------------------------------------

  const validateForm = (): boolean => {
    if (!form.nome.trim()) {
      toast.error("Il nome dell'evento e obbligatorio");
      return false;
    }
    if (!form.tipologiaId) {
      toast.error('Seleziona una tipologia');
      return false;
    }
    if (!isEdit && !form.authorCaricaId) {
      toast.error('Seleziona la carica con cui pubblichi l\'evento');
      return false;
    }
    if (!form.dataEvento) {
      toast.error("La data dell'evento e obbligatoria");
      return false;
    }
    if (!form.descrizione.trim()) {
      toast.error("Scrivi il tema dell'evento");
      return false;
    }
    if (!isEdit && !haOdg) {
      toast.error('Indica se l\'evento ha un ordine del giorno');
      return false;
    }
    if (haOdg === 'si' && !puntiOdg.some((x) => x.trim())) {
      toast.error('Scrivi almeno un punto dell\'ordine del giorno, oppure scegli «No»');
      return false;
    }
    return true;
  };

  /** Punti da inviare: `[]` = nessun ordine del giorno; `undefined` = scelta non fatta (resta com'è). */
  const ordineDelGiornoDaInviare = (): string[] | undefined => {
    if (haOdg === 'si') return puntiOdg.map((x) => x.trim()).filter(Boolean);
    if (haOdg === 'no') return [];
    return undefined;
  };

  const buildPayload = (stato?: 'BOZZA' | 'PUBBLICATO') => {
    const payload: any = { ...form };
    const odg = ordineDelGiornoDaInviare();
    if (odg !== undefined) payload.ordineDelGiorno = odg;
    if (stato) payload.stato = stato;
    if (!canSetTarget) delete payload.targetAudience;
    return payload;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (isEdit && id) {
        await updateRepEvent(id, buildPayload());
        toast.success('Evento aggiornato');
        navigate(`/area-associativa/calendario/${id}`);
      } else {
        const res = await createRepEvent(buildPayload('BOZZA'));
        const newId = res?.data?._id || res?.data?.id;
        toast.success('Evento creato');
        navigate(newId ? `/area-associativa/calendario/${newId}` : '/area-associativa/calendario');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // Classi del design system per i campi del form
  // -----------------------------------------------------------------------
  const inputCls = 'field';
  const selectCls = 'field';
  const labelCls = 'field-label';

  // -----------------------------------------------------------------------
  // Anteprima destinatari (riusata nelle due varianti della sezione Visibilita)
  // -----------------------------------------------------------------------

  const renderAudiencePreview = () => (
    <div className="pt-1">
      <button
        type="button"
        onClick={loadAudiencePreview}
        disabled={loadingPreview || !form.tipologiaId}
        className="btn soft sm"
        title="Mostra chi vedra l'evento nel calendario, a prescindere dagli inviti"
      >
        <Eye size={14} />
        {loadingPreview ? 'Calcolo...' : "Visualizza chi vedra l'evento"}
      </button>
      <p className="text-2xs text-gray-400 mt-1">Chi lo vede nel calendario, a prescindere dagli inviti.</p>
      {audiencePreview && (
        <div className="mt-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
            Vedranno l'evento {audiencePreview.total} rappresentanti unici su {audiencePreview.attivi} attivi
            {audiencePreview.multiCariche > 0 && (
              <span className="font-normal text-gray-500 dark:text-gray-400"> — {audiencePreview.multiCariche} hanno più cariche abilitanti e compaiono in più righe</span>
            )}
          </p>
          {audiencePreview.perCarica.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 max-h-36 overflow-y-auto">
              {audiencePreview.perCarica.map(r => (
                <li key={r.nome} className="flex justify-between text-2xs text-gray-600 dark:text-gray-400">
                  <span className="truncate pr-2">{r.nome}</span>
                  <span className="font-medium flex-shrink-0">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-2xs text-gray-400 dark:text-gray-500 mt-1.5">
            Ogni riga conta le persone sotto le cariche che ABILITANO la loro visibilit&agrave;
            (permesso eventi + settore compatibile, o carica destinataria); chi ha pi&ugrave; cariche
            abilitanti compare in pi&ugrave; righe, quindi la somma pu&ograve; superare il totale unico.
          </p>
        </div>
      )}
    </div>
  );

  // -----------------------------------------------------------------------
  // Loading / skeleton
  // -----------------------------------------------------------------------
  if (loading) {
    return (
      <AssociativeAppShell
        route="calendario"
        nome={profile?.nome}
        cognome={profile?.cognome}
        caricaPrincipale={highestCarica}
        tipo={profile?.tipo}
        foto={profile?.foto}
        associatesAccess={profile?.associatesAccess}
        canLive={canLive}
        canCircolari={canCircolari}
        canSondaggi={canSondaggi}
        canCalendario={canCalendario}
      >
        <div className="page fade-in">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48" />
            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </AssociativeAppShell>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <AssociativeAppShell
      route="calendario"
      nome={profile?.nome}
      cognome={profile?.cognome}
      caricaPrincipale={highestCarica}
      tipo={profile?.tipo}
      foto={profile?.foto}
      associatesAccess={profile?.associatesAccess}
      canLive={canLive}
      canCircolari={canCircolari}
      canSondaggi={canSondaggi}
      canCalendario={canCalendario}
    >
      <div className="page fade-in">
        {/* Back + Title */}
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate('/area-associativa/calendario')}
        >
          <ArrowLeft size={14} /> Calendario
        </button>
        <div className="flex flex-wrap items-center justify-between gap-3" style={{ margin: '8px 0 26px' }}>
          <h1
            style={{
              margin: 0,
              font: '700 25px/1.2 var(--font-display)',
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--text-strong)',
            }}
          >
            {isEdit ? 'Modifica Evento' : 'Nuovo Evento'}
          </h1>
          <VideoGuidaVerbali />
        </div>
        {!isEdit && (
          <ol aria-label="Passi per creare l'evento" className="mb-6 -mt-3 flex flex-wrap gap-2 text-sm">
            {['Dati e ordine del giorno', 'Invitati', 'Pubblica e invia'].map((t, i) => (
              <li
                key={t}
                aria-current={i === 0 ? 'step' : undefined}
                className={`flex items-center gap-2 rounded-full px-3 py-1 ${i === 0
                  ? 'bg-emerald-600 font-semibold text-white'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}
              >
                <span>{i + 1}</span>
                <span>{t}</span>
              </li>
            ))}
          </ol>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Informazioni Generali — obbligatori sempre visibili */}
          <section className="card">
            <div className="card-head">
              <Calendar size={16} />
              <h2>Informazioni Generali</h2>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Nome */}
              <div>
                <label className={labelCls}>
                  Nome evento <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="Es. Assemblea provinciale Roma"
                />
              </div>

              {/* Carica autrice (snapshot): obbligatoria se l'autore ha più cariche */}
              {!isEdit && activeCariche.length > 1 && (
                <div>
                  <label className={labelCls}>
                    Pubblichi come <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="authorCaricaId"
                    value={form.authorCaricaId}
                    onChange={(e) => {
                      const newCaricaId = e.target.value;
                      // se la tipologia scelta non è più creabile con la nuova carica, azzerala
                      const cat = activeCariche.find(c => c.id === newCaricaId)?.categoria;
                      const rank = caricaCreateRank(cat);
                      setForm(prev => {
                        const curType = eventTypes.find(t => t._id === prev.tipologiaId);
                        const stillOk = curType ? rank >= (AMBITO_RANK[curType.ambito || 'nazionale'] ?? 3) : true;
                        return { ...prev, authorCaricaId: newCaricaId, tipologiaId: stillOk ? prev.tipologiaId : '' };
                      });
                      setAudiencePreview(null);
                    }}
                    className={selectCls}
                  >
                    <option value="">Seleziona la carica...</option>
                    {activeCariche.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                  <p className="text-2xs text-gray-500 dark:text-gray-400 mt-1">
                    Determina chi vedrà l'evento (regole di visibilità per carica).
                  </p>
                </div>
              )}

              {/* Tipologia + Data */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    Tipologia <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="tipologiaId"
                      value={form.tipologiaId}
                      onChange={handleChange}
                      className={selectCls}
                    >
                      <option value="">Seleziona tipologia...</option>
                      {creatableTypes.map(t => (
                        <option key={t._id} value={t._id}>{t.nome}</option>
                      ))}
                    </select>
                    {selectedType && (
                      <span
                        className="absolute right-8 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: selectedType.colore }}
                      />
                    )}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>
                    Data Evento <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="dataEvento"
                    value={form.dataEvento}
                    onChange={handleChange}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Tema dell'evento — obbligatorio (l'ordine del giorno ha la sua sezione qui sotto) */}
              <div>
                <label className={labelCls}>
                  Tema dell'evento <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="descrizione"
                  value={form.descrizione}
                  onChange={handleChange}
                  rows={3}
                  className={`${inputCls} resize-y`}
                  placeholder="Di cosa si parla, in una o due frasi. Es.: Assemblea annuale degli studi della provincia."
                />
                <p className="mt-1 text-2xs text-gray-400 dark:text-gray-500">
                  Compare nell'email di convocazione. L'ordine del giorno lo scrivi nella sezione qui sotto, un punto alla volta.
                  Nel passo «Invitati» potrai anche allegare documenti (PDF o immagini) all'email di invito.
                </p>
              </div>
            </div>
          </section>

          {/* Ordine del giorno: domanda guidata e punti numerati */}
          <section className="card" aria-labelledby="titolo-odg">
            <div className="card-head">
              <ListOrdered size={16} />
              <h2 id="titolo-odg">Ordine del giorno</h2>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p className="text-sm text-gray-700 dark:text-gray-200">
                L'evento ha un ordine del giorno?{!isEdit && <span className="text-red-500"> *</span>}
              </p>
              <div role="radiogroup" aria-label="L'evento ha un ordine del giorno?" className="flex flex-wrap gap-2">
                {([['si', 'Sì, lo scrivo'], ['no', 'No']] as const).map(([valore, etichetta]) => (
                  <div
                    key={valore}
                    role="radio"
                    tabIndex={0}
                    aria-checked={haOdg === valore}
                    className="check-pill"
                    onClick={() => setHaOdg(valore)}
                    onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setHaOdg(valore); } }}
                  >
                    <span className="box">{haOdg === valore && <Check size={12} />}</span>
                    {etichetta}
                  </div>
                ))}
              </div>

              {haOdg === 'si' && (
                <div className="flex flex-col gap-2">
                  <p className="text-2xs text-gray-500 dark:text-gray-400">
                    Un punto per riga, per esempio «Comunicazioni del Segretario». Nella convocazione compaiono numerati
                    e il verbale li seguirà in quest'ordine.
                  </p>
                  {puntiOdg.map((punto, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-6 flex-shrink-0 text-right text-sm font-semibold text-gray-500 dark:text-gray-400">{i + 1}.</span>
                      <input
                        type="text"
                        value={punto}
                        maxLength={300}
                        aria-label={`Punto ${i + 1} dell'ordine del giorno`}
                        onChange={(e) => {
                          const valore = e.target.value;
                          setPuntiOdg((prev) => prev.map((x, j) => (j === i ? valore : x)));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (puntiOdg.length < 30) setPuntiOdg((prev) => [...prev.slice(0, i + 1), '', ...prev.slice(i + 1)]);
                          }
                        }}
                        className={inputCls}
                        placeholder={i === 0 ? 'Es. Comunicazioni del Segretario' : ''}
                      />
                      <button
                        type="button"
                        aria-label={`Togli il punto ${i + 1}`}
                        disabled={puntiOdg.length === 1}
                        onClick={() => setPuntiOdg((prev) => prev.filter((_, j) => j !== i))}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 dark:hover:bg-red-900/30"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPuntiOdg((prev) => [...prev, ''])}
                    disabled={puntiOdg.length >= 30}
                    className="top-link"
                    style={{ alignSelf: 'flex-start', padding: '4px 2px', color: 'var(--un-green-700)' }}
                  >
                    <Plus size={14} /> Aggiungi punto
                  </button>
                </div>
              )}
              {haOdg === 'no' && (
                <p className="text-2xs text-gray-500 dark:text-gray-400">
                  Va bene: nel verbale la sezione «Ordine del giorno» non ci sarà e l'assistente non la chiederà.
                </p>
              )}
              {haOdg === '' && isEdit && (
                <p className="text-2xs text-gray-500 dark:text-gray-400">
                  Per gli eventi creati prima l'ordine del giorno può essere nel tema: se vuoi, riportalo qui punto per punto.
                </p>
              )}
            </div>
          </section>

          {/* Toggle dettagli facoltativi */}
          <button
            type="button"
            onClick={() => setShowOptional(v => !v)}
            className="top-link"
            style={{ alignSelf: 'flex-start', padding: '6px 4px', color: 'var(--un-green-700)' }}
          >
            {showOptional ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            {showOptional ? 'Nascondi dettagli facoltativi' : 'Aggiungi orario, luogo, note interne…'}
          </button>

          {showOptional && (
            <>
              {/* Orari */}
              <section className="card">
                <div className="card-head">
                  <Clock size={16} />
                  <h2>Orario</h2>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Ora Inizio</label>
                      <div className="relative">
                        <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none z-10" />
                        <select
                          name="oraInizio"
                          value={form.oraInizio}
                          onChange={handleChange}
                          className={`${selectCls} pl-8`}
                        >
                          <option value="">--:--</option>
                          {TIME_OPTIONS.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Ora Fine</label>
                      <div className="relative">
                        <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none z-10" />
                        <select
                          name="oraFine"
                          value={form.oraFine}
                          onChange={handleChange}
                          className={`${selectCls} pl-8`}
                        >
                          <option value="">--:--</option>
                          {TIME_OPTIONS.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Luogo */}
              <section className="card">
                <div className="card-head">
                  <MapPin size={16} />
                  <h2>Luogo</h2>
                </div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Luogo</label>
                      <input
                        type="text"
                        name="luogo"
                        value={form.luogo}
                        onChange={handleChange}
                        className={inputCls}
                        placeholder="Es. Hotel Excelsior"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Indirizzo</label>
                      <input
                        type="text"
                        name="indirizzo"
                        value={form.indirizzo}
                        onChange={handleChange}
                        className={inputCls}
                        placeholder="Es. Via Roma 1, 00100 Roma"
                      />
                    </div>
                  </div>

                  {/* Link Mappa */}
                  <div>
                    <label className={labelCls}>
                      <Link2 className="w-3 h-3 inline mr-1 -mt-0.5" />
                      Link Google Maps
                    </label>
                    <input
                      type="text"
                      name="linkMappa"
                      value={form.linkMappa}
                      onChange={handleChange}
                      className={inputCls}
                      placeholder="https://maps.google.com/..."
                    />
                    <p className="mt-1 text-2xs text-gray-400 dark:text-gray-500">
                      Apri Google Maps, cerca il luogo, clicca "Condividi" &rarr; "Copia link" e incollalo qui.
                    </p>
                  </div>
                </div>
              </section>

              {/* Dettagli */}
              <section className="card">
                <div className="card-head">
                  <FileText size={16} />
                  <h2>Dettagli</h2>
                </div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label className={labelCls}>Note Interne</label>
                    <textarea
                      name="noteInterne"
                      value={form.noteInterne}
                      onChange={handleChange}
                      rows={3}
                      className={`${inputCls} resize-y`}
                      placeholder="Note riservate, non visibili ai partecipanti..."
                    />
                  </div>
                </div>
              </section>
            </>
          )}

          {/* Promemoria automatico — opt-in (default OFF), solo 24h per i rappresentanti */}
          <section className="card">
            <div className="card-head">
              <Bell size={16} />
              <h2>Promemoria automatico</h2>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div
                className="check-pill"
                aria-checked={form.reminder24h}
                onClick={() => setForm(prev => ({ ...prev, reminder24h: !prev.reminder24h }))}
              >
                <span className="box">{form.reminder24h && <Check size={12} />}</span>
                Invia un promemoria automatico agli invitati 24 ore prima dell'evento
              </div>
              <p className="text-2xs text-gray-400 dark:text-gray-500">
                Disattivato di default. Se attivo, gli invitati che hanno gi&agrave; ricevuto l'invito
                ricevono un'email di promemoria il giorno prima. Nessun promemoria viene inviato senza questa scelta.
              </p>
            </div>
          </section>

          {/* Settori dell'evento (dai permessi settore.* dell'autore) */}
          <section className="card">
            <div className="card-head">
              <FileText size={16} />
              <h2>Settori dell'evento</h2>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {authorSettori.length === 0 ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 rounded-lg p-3">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                    Le tue cariche non hanno settori assegnati nei permessi
                  </p>
                  <p className="text-2xs text-amber-700 dark:text-amber-400 mt-0.5">
                    L'evento non sar&agrave; visibile agli altri rappresentanti (lo vedrai solo tu e gli eventuali invitati).
                    Contatta la segreteria per l'assegnazione dei settori alla tua carica.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-2xs text-gray-400 dark:text-gray-500">
                    L'evento &egrave; visibile solo ai rappresentanti che hanno almeno uno di questi settori nei permessi
                    delle loro cariche. Derivano dai tuoi permessi: puoi deselezionarne alcuni per restringere.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {authorSettori.map(s => {
                      const on = form.settori.includes(s);
                      return (
                        <div
                          key={s}
                          className="check-pill"
                          aria-checked={on}
                          onClick={() => handleSettoreEventoToggle(s)}
                        >
                          <span className="box">{on && <Check size={12} />}</span>
                          {SETTORE_EVENT_LABELS[s] || s}
                        </div>
                      );
                    })}
                  </div>
                  {form.settori.length === 0 && (
                    <p className="text-2xs text-amber-600 dark:text-amber-400">
                      Nessun settore selezionato: l'evento non sar&agrave; visibile agli altri rappresentanti.
                    </p>
                  )}
                </>
              )}
            </div>
          </section>

          {/* Visibilita (solo con permesso) */}
          {canSetTarget && (
            <section className="card">
              <div className="card-head">
                <Eye size={16} />
                <h2>Visibilita</h2>
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <p className="text-2xs text-gray-400 dark:text-gray-500">
                  La visibilit&agrave; di base la decidono le <strong>policy delle cariche</strong>. Qui puoi
                  <strong> facoltativamente restringere</strong> i destinatari: non puoi allargare oltre ci&ograve; che le policy consentono.
                </p>
                <div className="seg">
                  <button
                    type="button"
                    aria-pressed={form.targetAudience.scope === 'tutti'}
                    onClick={() => handleTargetScopeChange('tutti')}
                  >
                    Nessuna restrizione
                  </button>
                  <button
                    type="button"
                    aria-pressed={form.targetAudience.scope === 'selettivo'}
                    onClick={() => handleTargetScopeChange('selettivo')}
                  >
                    Restringi destinatari
                  </button>
                </div>

                {form.targetAudience.scope === 'selettivo' && (
                  <div
                    className="space-y-4"
                    style={{ paddingLeft: '16px', borderLeft: '2px solid var(--un-green-100)' }}
                  >
                    <p className="text-2xs text-gray-400 dark:text-gray-500">
                      Restringe a chi rientra in questi criteri (in <strong>AND</strong> con le policy).
                      Livelli e cariche indicano <strong>chi</strong>; regioni e province il <strong>territorio</strong>.
                      Se compili entrambe le dimensioni devono valere entrambe (es. "Provinciale" + "Abruzzo" = solo i provinciali abruzzesi).
                    </p>
                    <div>
                      <label className={labelCls}>Livelli</label>
                      <div className="flex flex-wrap gap-2">
                        {LIVELLO_OPTIONS.map(opt => {
                          const on = form.targetAudience.livelli.includes(opt.value);
                          return (
                            <div
                              key={opt.value}
                              className="check-pill"
                              aria-checked={on}
                              onClick={() => handleLivelloToggle(opt.value)}
                            >
                              <span className="box">{on && <Check size={12} />}</span>
                              {opt.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {caricheOptions.length > 0 && (
                      <div>
                        <label className={labelCls}>
                          Cariche specifiche
                        </label>
                        <p className="text-2xs text-gray-400 dark:text-gray-500 mb-2 -mt-1">
                          Raggiunge chi ha la carica, anche se ne ricopre altre
                        </p>
                        <div className="max-h-44 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {caricheOptions.map(c => {
                            const on = form.targetAudience.cariche.includes(c._id);
                            return (
                              <div
                                key={c._id}
                                className="check-pill"
                                aria-checked={on}
                                onClick={() => handleCaricaToggle(c._id)}
                              >
                                <span className="box">{on && <Check size={12} />}</span>
                                <span className="truncate">{c.nome}</span>
                              </div>
                            );
                          })}
                        </div>
                        {form.targetAudience.cariche.length > 0 && (
                          <p className="text-2xs text-emerald-600 dark:text-emerald-400 mt-2">{form.targetAudience.cariche.length} cariche selezionate</p>
                        )}
                      </div>
                    )}

                    <div>
                      <label className={labelCls}>
                        Regioni <span className="normal-case">(separate da virgola)</span>
                      </label>
                      <input
                        type="text"
                        value={form.targetAudience.regioni.join(', ')}
                        onChange={e => handleTargetTextChange('regioni', e.target.value)}
                        className={inputCls}
                        placeholder="Es. Lazio, Lombardia"
                      />
                    </div>

                    <div>
                      <label className={labelCls}>
                        Province <span className="normal-case">(separate da virgola)</span>
                      </label>
                      <input
                        type="text"
                        value={form.targetAudience.province.join(', ')}
                        onChange={e => handleTargetTextChange('province', e.target.value)}
                        className={inputCls}
                        placeholder="Es. RM, MI, NA"
                      />
                    </div>
                  </div>
                )}

                {renderAudiencePreview()}
              </div>
            </section>
          )}

          {/* Chi NON ha il permesso di restringere non vede alcuna sezione di
              visibilita (la decide la policy delle cariche): si evita confusione. */}

          {/* Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '4px', paddingBottom: '16px' }}>
            <button
              type="button"
              onClick={() => navigate('/area-associativa/calendario')}
              disabled={saving}
              className="btn ghost"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="btn primary"
            >
              {!saving && <Save size={15} />}
              {saving ? 'Salvataggio...' : (isEdit ? 'Salva modifiche' : 'Crea evento e scegli gli invitati')}
            </button>
          </div>
        </div>
      </div>
    </AssociativeAppShell>
  );
};

export default AssociativeEventEditor;
